/**
 * Spinner — canonical loading indicator for the app.
 *
 * Replaces the ad-hoc border-spinner div that was copy-pasted across
 * page-load / auth / Suspense fallbacks:
 *
 *   <div className="w-10 h-10 border-2 border-primary/30 border-t-primary
 *                   rounded-full animate-spin" />
 *
 * (Note: most in-button / inline spinners use lucide's `<Loader2 className="animate-spin" />`
 *  — that is a separate shared icon and is intentionally NOT replaced by this.)
 *
 * Color follows `currentColor`, so the ring inherits the text color of the
 * element or its parent. Put `text-primary` (default intent for page loaders)
 * or `text-muted-foreground` on the Spinner or an ancestor to color it; inside
 * a primary `<Button>` it inherits `text-primary-foreground` automatically.
 *
 * A11y:
 *  - By default the Spinner is its own live region: `role="status"` with an
 *    `sr-only` label (default "Loading"), and the visual ring is `aria-hidden`.
 *  - When the Spinner sits inside a block that ALREADY announces its own status
 *    (e.g. a `<div role="status">…<p>Checking your session…</p></div>` wrapper,
 *    or a `<Button>` whose text says "Sending…"), pass `decorative` so the
 *    Spinner renders as a pure `aria-hidden` visual — avoiding a redundant,
 *    nested `role="status"` / double announcement.
 *
 * Usage:
 *
 *   // Standalone page loader (announces "Loading"):
 *   <Spinner size="lg" className="text-primary" />
 *
 *   // Inside an already-announced status block (ring only):
 *   <div role="status" aria-live="polite" className="text-center">
 *     <Spinner size="xl" decorative className="text-primary mx-auto mb-4" />
 *     <p className="text-muted-foreground">Checking your session…</p>
 *   </div>
 */

import * as React from 'react';
import { cn } from '@/lib/utils';

export type SpinnerSize = 'sm' | 'md' | 'lg' | 'xl';

const sizeClasses: Record<SpinnerSize, string> = {
  sm: 'w-4 h-4 border-2',
  md: 'w-6 h-6 border-2',
  lg: 'w-8 h-8 border-2',
  xl: 'w-10 h-10 border-2',
};

export interface SpinnerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'role' | 'aria-label'> {
  /** Visual size. sm=16px, md=24px, lg=32px, xl=40px. Default `md`. */
  size?: SpinnerSize;
  /**
   * Render as a purely decorative ring (`aria-hidden`, no `role="status"`).
   * Use when a parent element already announces the loading state, to avoid a
   * redundant / nested live region.
   */
  decorative?: boolean;
  /** Accessible label announced to screen readers. Default "Loading". Ignored when `decorative`. */
  label?: string;
}

/**
 * Accessible, size-controlled loading spinner. Color is driven by `currentColor`
 * (`border-current/30 border-t-current`) so `text-*` on the element or a parent
 * sets the ring color.
 */
export function Spinner({
  size = 'md',
  decorative = false,
  label = 'Loading',
  className,
  ...props
}: SpinnerProps) {
  const ring = cn(
    sizeClasses[size],
    'border-current/30 border-t-current rounded-full animate-spin',
    className,
  );

  if (decorative) {
    return <div aria-hidden="true" className={ring} {...props} />;
  }

  return (
    <div role="status" {...props}>
      <div aria-hidden="true" className={ring} />
      <span className="sr-only">{label}</span>
    </div>
  );
}
